// #489 Etappe V3: „Vorgang starten“ aus der Mail – reine Funktionen (testbar).
// Aus Vorgangsart + KI-Angaben wird ein Vorgang: Ticket mit Vorgangsart, Checkliste, Frist,
// Wiedervorlage und Zuständigkeit; die Antwort an den Absender fragt fehlende Angaben nach.
import type { TicketVorlage } from '../ki/ticketVorlage';
import { fuegeChecklisteAn } from '../tickets/checklistenVorlagen';
import type { Vorgangsart } from './vorgangsarten';

export type AngabeStatus = 'mail' | 'app' | 'intern' | 'fehlt';

export interface AngabeZeile {
  schluessel: string;
  bezeichnung: string;
  quelle: 'app' | 'absender';
  status: AngabeStatus;
  wert: string | null;
}

/** Was die App aus der Zuordnung der Mail sicher weiß. */
export interface Bekannt { mieter: boolean; einheit: boolean }

// Angaben mit quelle 'app': welche Zuordnung sie brauchen. Unbekannte Schlüssel brauchen ein Mietverhältnis.
const APP_BRAUCHT: Record<string, keyof Bekannt> = { mieter: 'mieter', einheit: 'einheit' };

/**
 * Status je Pflichtangabe:
 * - mail: steht in der Mail (Wert von der KI)
 * - app: kennt die App (Mieter/Einheit zugeordnet)
 * - intern: kennt die App noch nicht – prüft das Team, wird NICHT beim Absender erfragt
 * - fehlt: beim Absender erfragen
 */
export function bewerteAngaben(art: Pick<Vorgangsart, 'pflichtangaben'>, ausMail: Record<string, string> | null | undefined, bekannt: Bekannt): AngabeZeile[] {
  return art.pflichtangaben.map(a => {
    const wert = (ausMail?.[a.schluessel] ?? '').trim() || null;
    if (wert) return { ...a, status: 'mail', wert };
    if (a.quelle === 'app') {
      const braucht = APP_BRAUCHT[a.schluessel] ?? 'mieter';
      return { ...a, status: bekannt[braucht] ? 'app' : 'intern', wert: null };
    }
    return { ...a, status: 'fehlt', wert: null };
  });
}

/** Bezeichnungen der Angaben, die beim Absender nachgefragt werden. */
export function rueckfragen(zeilen: readonly AngabeZeile[]): string[] {
  return zeilen.filter(z => z.status === 'fehlt').map(z => z.bezeichnung);
}

const GRUSS = /\n\s*(mit freundlichen grüßen|freundliche grüße|viele grüße|beste grüße)/i;

/** Rückfragen als Liste vor den Gruß setzen (sonst ans Ende). */
export function ergaenzeRueckfragen(text: string, fragen: readonly string[]): string {
  if (fragen.length === 0) return text;
  const block = `Damit wir Ihr Anliegen zügig bearbeiten können, benötigen wir noch folgende Angaben:\n${fragen.map(f => `- ${f}`).join('\n')}`;
  const m = GRUSS.exec(text);
  if (!m) return `${text.trimEnd()}\n\n${block}`;
  return `${text.slice(0, m.index).trimEnd()}\n\n${block}\n\n${text.slice(m.index).replace(/^\s+/, '')}`;
}

function plusTage(heute: Date, tage: number): string {
  const d = new Date(Date.UTC(heute.getFullYear(), heute.getMonth(), heute.getDate() + tage));
  return d.toISOString().slice(0, 10);
}

/** Gespeicherte Form der Angaben am Ticket (tickets.vorgang_angaben). */
export function angabenFuerTicket(zeilen: readonly AngabeZeile[]): Record<string, { bezeichnung: string; status: AngabeStatus; wert: string | null }> {
  return Object.fromEntries(zeilen.map(z => [z.schluessel, { bezeichnung: z.bezeichnung, status: z.status, wert: z.wert }]));
}

export interface VorgangsTicket extends TicketVorlage {
  vorgangsart_id: string;
  vorgang_angaben: ReturnType<typeof angabenFuerTicket>;
  wiedervorlage_am: string | null;
  zustaendig_id: string | null;
  zustaendig_typ: 'mitarbeiter' | 'team' | null;
}

/**
 * Ticket-Vorlage aus der Analyse um den Vorgang ergänzen: Kategorie und Zuständigkeit der
 * Vorgangsart (wenn gesetzt), Frist/Wiedervorlage ab heute, Checkliste unten an der Beschreibung,
 * rechtlich sensibel, wenn Mail ODER Vorgangsart es sind.
 */
export function vorgangsTicket(basis: TicketVorlage, art: Vorgangsart, zeilen: readonly AngabeZeile[], checkliste: string | null, heute: Date): VorgangsTicket {
  const vorlage: VorgangsTicket = {
    ...basis,
    beschreibung: checkliste ? fuegeChecklisteAn(basis.beschreibung, checkliste) : basis.beschreibung,
    vorgangsart_id: art.id,
    vorgang_angaben: angabenFuerTicket(zeilen),
    wiedervorlage_am: art.wiedervorlage_tage != null ? plusTage(heute, art.wiedervorlage_tage) : null,
    zustaendig_id: art.zustaendig_id ?? null,
    zustaendig_typ: art.zustaendig_id ? art.zustaendig_typ : null,
    ist_rechtlich_sensibel: !!basis.ist_rechtlich_sensibel || art.rechtlich_sensibel,
  };
  if (art.kategorie_id) {
    vorlage.kategorie_id = art.kategorie_id;
    vorlage.unterkategorie_id = art.unterkategorie_id ?? null;
  }
  if (art.faellig_tage != null) vorlage.faellig_am = plusTage(heute, art.faellig_tage);
  return vorlage;
}
